import type {
  TEncodedBody,
  THttpInit,
  THttpRequest,
  THttpRequestOptions,
} from './http.types.ts';
import { filterEntries } from '#utils/object/index.ts';

const JSON_TYPE = 'application/json';
const MULTIPART = 'multipart/form-data';

const ownOptionKeys = new Set([
  'query',
  'headers',
  'contentType',
  'responseType',
  'signal',
  'timeout',
]);

const isBlobList = (value: unknown): value is Blob[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every((item) => item instanceof Blob);

export const toFormData = (payload: object): FormData => {
  const form = new FormData();

  for (const [name, value] of Object.entries(payload)) {
    if (value === undefined) {
      continue;
    }

    if (value instanceof Blob) {
      form.append(name, value);
    } else if (isBlobList(value)) {
      value.forEach((blob) => form.append(name, blob));
    } else {
      form.append(name, new Blob([JSON.stringify(value)], { type: JSON_TYPE }));
    }
  }

  return form;
};

export const isRawBody = (
  body: unknown,
): body is NonNullable<RequestInit['body']> =>
  typeof body === 'string' ||
  body instanceof Blob ||
  body instanceof FormData ||
  body instanceof URLSearchParams ||
  body instanceof ArrayBuffer ||
  body instanceof ReadableStream ||
  ArrayBuffer.isView(body);

export const encodeBody = (
  body: unknown,
  contentType: string | undefined,
): TEncodedBody => {
  if (body === undefined) {
    return { body: undefined, headers: {} };
  }

  if (contentType === MULTIPART) {
    return { body: toFormData(body as object), headers: {} };
  }

  if (isRawBody(body)) {
    return { body, headers: {} };
  }

  return {
    body: JSON.stringify(body),
    headers: { 'Content-Type': JSON_TYPE },
  };
};

export const passthroughOf = (options: THttpRequestOptions): THttpInit =>
  filterEntries(options, (_value, key) => !ownOptionKeys.has(key)) as THttpInit;

export const buildRequest = (
  { method, body, options, headers, init }: THttpRequest,
  signal: AbortSignal,
): RequestInit => {
  const {
    headers: requestHeaders,
    contentType,
    responseType = 'json',
  } = options;

  const encoded = encodeBody(body, contentType);

  return {
    ...init,
    ...passthroughOf(options),
    method,
    body: encoded.body,
    headers: {
      ...(responseType === 'json' ? { Accept: JSON_TYPE } : {}),
      ...encoded.headers,
      ...headers,
      ...requestHeaders,
    },
    signal,
  };
};
